import { State } from 'flow/types/State';
import { TicketKey } from 'flow/types/EntityKeys';
import { TicketPermission, TicketViewerPermissions } from 'flow/types/Tickets';
import { TypedMap } from 'flow/types/TypedMap';
import { selectTicket } from './TicketsSelectors';

/**
 * What the viewer may do to a ticket. The backend resolves it per viewer and per
 * ticket, having already consumed the capability, membership, the ticket's
 * visibility, its origin and the channel a reply leaves on — so nothing in this
 * file re-derives any of that, and nothing may AND a capability check back on top.
 * That is the whole point: five clients release on different cadences, and a rule
 * stated twice drifts.
 */

const selectTicketViewerPermissions = (
    state: State,
    ticketKey: TicketKey,
): TypedMap<TicketViewerPermissions> | undefined =>
    selectTicket(state, ticketKey)?.get('viewerPermissions');

/**
 * The one place a control asks whether it may act.
 *
 * An absent block denies. Every endpoint that returns a ticket to a logged-in user
 * now resolves it, so absent means the ticket predates this deploy or was never
 * meant to carry controls — in both cases showing none is the honest answer.
 *
 * Stays one selector taking the permission rather than one per act: the argument is
 * `keyof TicketViewerPermissions`, so a wrong name is already a compile error, and
 * adding an act stays a one-place change.
 */
export const selectOwnUserHasTicketPermission = (
    state: State,
    ticketKey: TicketKey,
    permission: TicketPermission,
): boolean =>
    !!selectTicketViewerPermissions(state, ticketKey)?.get(permission);

/** Named because several tag components ask for it; it is just the resolved answer. */
export const selectOwnUserCanEditTags = (state: State, ticketKey: TicketKey) =>
    selectOwnUserHasTicketPermission(state, ticketKey, 'editTags');
